import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight, Crown, Download, LifeBuoy, LogOut, Shield, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { TierPill } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { updatePreferences, useAppConfig } from "@/hooks/queries";
import { useInstallPrompt } from "@/hooks/useBrowser";
import { errorMessage } from "@/lib/api";
import { initials } from "@/lib/format";

const RowShell = ({ icon: Icon, label, hint, right, ...props }) => (
  <button type="button" className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-elevated/60" {...props}>
    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-elevated text-gold">
      <Icon className="h-4 w-4" strokeWidth={1.7} />
    </span>
    <span className="flex-1">
      <span className="block text-sm text-ivory">{label}</span>
      {hint && <span className="block text-xs text-faint">{hint}</span>}
    </span>
    {right || <ChevronRight className="h-4 w-4 text-faint" />}
  </button>
);

function InfoRow({ icon, label, hint, title, children, testId }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <RowShell icon={icon} label={label} hint={hint} data-testid={testId} />
      </DialogTrigger>
      <DialogContent className="rounded-3xl border-line bg-surface" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          <DialogDescription className="text-mist">{label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-mist">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

function NotificationsRow() {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const toggle = async (v) => {
    setSaving(true);
    try {
      setUser(await updatePreferences({ ride_reminders: v }));
      toast.success(v ? "Ride reminder emails are on" : "Ride reminder emails are off");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <label htmlFor="notifications-switch" className="flex w-full cursor-pointer items-center gap-4 px-5 py-4 transition-colors hover:bg-elevated/60" data-testid="settings-notifications-row">
      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-elevated text-gold">
        <Bell className="h-4 w-4" strokeWidth={1.7} />
      </span>
      <span className="flex-1">
        <span className="block text-sm text-ivory">Ride reminder emails</span>
        <span className="block text-xs text-faint">An email the day before each ride</span>
      </span>
      <Switch id="notifications-switch" checked={user.ride_reminders} disabled={saving} onCheckedChange={toggle} data-testid="settings-notifications-switch" />
    </label>
  );
}

function DeleteAccount() {
  const { deleteAccount } = useAuth();
  const navigate = useNavigate();
  const run = async () => {
    try {
      await deleteAccount();
      toast.success("Your account has been deleted");
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" className="inline-flex items-center gap-2 text-sm text-cancelled/80 hover:text-cancelled" data-testid="delete-account-btn">
          <Trash2 className="h-4 w-4" /> Delete account
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-3xl border-line bg-surface">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">Delete your account?</AlertDialogTitle>
          <AlertDialogDescription className="text-mist">
            You'll be signed out and will no longer be able to access your trips or concierge history.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="delete-account-cancel-btn">Keep account</AlertDialogCancel>
          <AlertDialogAction onClick={run} className="rounded-full bg-cancelled text-ink hover:bg-cancelled/90" data-testid="delete-account-confirm-btn">
            Delete account
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: config } = useAppConfig();
  const { canInstall, install, standalone, isIOS } = useInstallPrompt();
  const since = new Date(user.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="space-y-8" data-testid="profile-page">
      <div className="rise relative overflow-hidden rounded-3xl border border-line">
        <img src="/images/hero-sclass.jpg" alt="" className="h-40 w-full object-cover sm:h-56" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent" />
        <div className="relative -mt-14 flex flex-col items-start gap-4 bg-surface px-6 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-end gap-4">
            <span className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-gold bg-ink font-display text-3xl text-gold shadow-[0_0_30px_rgba(204,148,64,0.25)]" data-testid="profile-avatar">
              {user.picture ? (
                <img src={user.picture} alt="" referrerPolicy="no-referrer" className="h-full w-full rounded-full object-cover" />
              ) : (
                initials(user.name)
              )}
            </span>
            <div className="pb-1">
              <h1 className="text-2xl text-ivory sm:text-3xl" data-testid="profile-name">{user.name}</h1>
              <p className="text-sm text-mist" data-testid="profile-email">{user.email}</p>
            </div>
          </div>
          <TierPill tier={user.membership_tier} testId="profile-membership-tier" />
        </div>
      </div>

      <div className="lux-card rise stagger-1 divide-y divide-line overflow-hidden" data-testid="settings-list">
        <InfoRow icon={Wallet} label="Payment methods" hint="PayPal · Cash App" title="Payment methods" testId="settings-payment-row">
          <p>Settle any ride from its trip page using hosted, secure links:</p>
          <p className="text-ivory">PayPal — paypal.me/{config?.paypal_handle}</p>
          <p className="text-ivory">Cash App — ${config?.cashtag}</p>
        </InfoRow>
        <InfoRow icon={Crown} label="Membership" hint={`${user.membership_tier} · Member since ${since}`} title={`${user.membership_tier} membership`} testId="settings-membership-row">
          <p>You are a {user.membership_tier} member of Luxury Elite ATL since {since}.</p>
          <p>Your dedicated concierge is available from the Concierge tab at any hour.</p>
        </InfoRow>
        <NotificationsRow />
        <InfoRow icon={Shield} label="Privacy" hint="How your details are used" title="Privacy" testId="settings-privacy-row">
          <p>Your name, contact details, locations and messages are used only to arrange your rides and concierge requests.</p>
          <p>Deleting your account signs you out and removes your access to trips and conversation history.</p>
        </InfoRow>
        <RowShell icon={LifeBuoy} label="Support" hint="Reach your concierge" onClick={() => navigate("/concierge")} data-testid="settings-support-row" />
        {!standalone && (canInstall || isIOS) && (
          <RowShell
            icon={Download}
            label="Install the app"
            hint={isIOS ? "Tap Share, then “Add to Home Screen”" : "Add Luxury Elite to your home screen"}
            onClick={() => (canInstall ? install() : toast("Tap Share, then “Add to Home Screen”"))}
            data-testid="settings-install-row"
          />
        )}
      </div>

      <div className="rise stagger-2 flex flex-wrap items-center justify-between gap-4">
        <button type="button" onClick={signOut} className="btn-ghost" data-testid="sign-out-btn">
          <LogOut className="h-4 w-4 text-gold" /> Sign out
        </button>
        <DeleteAccount />
      </div>
    </div>
  );
}
